/**
 * NBH primitives for the web-admin — mirrors the mobile resident app's
 * NBH (NoBrokerHood / MyGate style) feature-tile + pastel-section
 * design language. See apps/mobile/apps/resident/src/components/
 * (FeatureTile, NbhSectionHeader, NbhPageHeader).
 *
 * Colours are applied via inline `style` so they always render —
 * Tailwind's JIT can't always pick up dynamic class concatenations,
 * and we want the brand to be vivid and reliable.
 */
import { Link } from '@tanstack/react-router';
import type { LinkOptions } from '@tanstack/react-router';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type NbhPaletteKey =
  | 'peach'
  | 'mint'
  | 'lavender'
  | 'sky'
  | 'yellow'
  | 'salmon'
  | 'teal'
  | 'blush';

interface PaletteHex {
  /** Mid-tone tile background — saturated enough to read as "colorful". */
  bg: string;
  /** Brighter top-left for a 2-stop gradient. */
  bgFrom: string;
  /** Slightly deeper bottom-right gradient stop. */
  bgTo: string;
  /** Vivid foreground — used for icon disc + label text. */
  fg: string;
  /** Darker foreground for label text contrast on the brighter bg. */
  fgDeep: string;
}

// Saturated NBH palette — brighter than the mobile pastels so the
// tiles really pop on web (larger viewport reads pastels as washed-out).
const PALETTE: Record<NbhPaletteKey, PaletteHex> = {
  peach: { bg: '#ffd1b3', bgFrom: '#ffe0c2', bgTo: '#ffb88a', fg: '#c85a2e', fgDeep: '#8a3a17' },
  mint: { bg: '#a8e4c3', bgFrom: '#c4ecd6', bgTo: '#80d4a7', fg: '#1b7a3e', fgDeep: '#0e4f27' },
  lavender: { bg: '#cfc2ff', bgFrom: '#ddd4ff', bgTo: '#b8a7ff', fg: '#5b3fb8', fgDeep: '#3d2785' },
  sky: { bg: '#b5dcff', bgFrom: '#cae6ff', bgTo: '#8cc7ff', fg: '#0a5fbf', fgDeep: '#053f80' },
  yellow: { bg: '#ffe084', bgFrom: '#ffeaa8', bgTo: '#ffd25c', fg: '#9a7300', fgDeep: '#6b5000' },
  salmon: { bg: '#ffaeae', bgFrom: '#ffc4c4', bgTo: '#ff8a8a', fg: '#b03030', fgDeep: '#7a1f1f' },
  teal: { bg: '#a3e3df', bgFrom: '#bdebe8', bgTo: '#7ed4cd', fg: '#0e7a73', fgDeep: '#08524d' },
  blush: { bg: '#ffb4d2', bgFrom: '#ffc8de', bgTo: '#ff8eba', fg: '#a52a6a', fgDeep: '#6e1c47' }
};

export function nbhPalette(key: NbhPaletteKey): PaletteHex {
  return PALETTE[key];
}

// ─── NbhSectionHeader ─────────────────────────────────────────────
// 4px pastel stripe + title (16/700) + optional subtitle + right slot.

interface NbhSectionHeaderProps {
  title: string;
  subtitle?: string;
  palette?: NbhPaletteKey;
  rightAction?: React.ReactNode;
  className?: string;
}

export function NbhSectionHeader({
  title,
  subtitle,
  palette = 'sky',
  rightAction,
  className
}: NbhSectionHeaderProps): JSX.Element {
  const p = nbhPalette(palette);
  return (
    <div className={cn('flex items-center gap-vt-3 px-vt-1 py-vt-2', className)}>
      <span
        aria-hidden="true"
        className="h-6 w-1 shrink-0 rounded-full"
        style={{ backgroundColor: p.fg }}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-vt-16 font-bold tracking-[-0.01em] text-vt-text">{title}</div>
        {subtitle ? (
          <div className="mt-0.5 truncate text-vt-12 font-medium text-vt-text-muted">{subtitle}</div>
        ) : null}
      </div>
      {rightAction ? <div className="shrink-0">{rightAction}</div> : null}
    </div>
  );
}

// ─── NbhStatTile ──────────────────────────────────────────────────
// Pastel KPI block: vivid colored icon disc, white icon inside, big value, label.

export type TrendDirection = 'up' | 'down' | 'flat';

interface NbhStatTileProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon: LucideIcon;
  palette: NbhPaletteKey;
  to?: LinkOptions['to'];
  delta?: string;
  trend?: TrendDirection;
  className?: string;
}

const TREND_HEX: Record<TrendDirection, { bg: string; fg: string; Icon: LucideIcon }> = {
  up: { bg: '#dcfce7', fg: '#15803d', Icon: TrendingUp },
  down: { bg: '#fee2e2', fg: '#b91c1c', Icon: TrendingDown },
  flat: { bg: '#e2e8f0', fg: '#475569', Icon: Minus }
};

export function NbhStatTile({
  label,
  value,
  sub,
  icon: Icon,
  palette,
  to,
  delta,
  trend = 'flat',
  className
}: NbhStatTileProps): JSX.Element {
  const p = nbhPalette(palette);
  const t = TREND_HEX[trend];
  const TrendIcon = t.Icon;
  const body = (
    <div
      className={cn(
        'group relative flex flex-col gap-vt-3 overflow-hidden rounded-vt-lg p-vt-5',
        'shadow-vt-1 transition-all duration-vt ease-vt',
        to && 'hover:-translate-y-px hover:shadow-vt-2',
        className
      )}
      style={{ background: `linear-gradient(135deg, ${p.bgFrom} 0%, ${p.bgTo} 100%)` }}
    >
      <span aria-hidden="true" className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/45" />
      <span aria-hidden="true" className="absolute -right-2 -top-2 h-8 w-8 rounded-full bg-white/30" />

      <div className="relative flex items-start justify-between gap-vt-3">
        <div className="min-w-0">
          <div
            className="text-vt-12 font-semibold uppercase tracking-[0.08em]"
            style={{ color: p.fgDeep }}
          >
            {label}
          </div>
          <div className="mt-vt-2 flex items-baseline gap-vt-2">
            <span
              className="text-vt-32 font-bold leading-none tracking-[-0.02em]"
              style={{ color: p.fgDeep }}
            >
              {value}
            </span>
            {delta ? (
              <span
                className="inline-flex items-center gap-1 rounded-vt-pill px-2 py-0.5 text-[11px] font-semibold leading-none"
                style={{ backgroundColor: t.bg, color: t.fg }}
              >
                <TrendIcon className="h-3 w-3" strokeWidth={2.25} aria-hidden="true" />
                {delta}
              </span>
            ) : null}
          </div>
          {sub ? (
            <div className="mt-vt-2 text-vt-12 font-semibold" style={{ color: p.fg }}>
              {sub}
            </div>
          ) : null}
        </div>
        <span
          aria-hidden="true"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full shadow-vt-2 ring-4 ring-white/60 transition-transform duration-vt ease-vt group-hover:scale-110"
          style={{ backgroundColor: p.fg }}
        >
          <Icon className="h-6 w-6 text-white" strokeWidth={2.5} />
        </span>
      </div>
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="block rounded-vt-lg outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400">
        {body}
      </Link>
    );
  }
  return body;
}

// ─── NbhFeatureTile ───────────────────────────────────────────────
// Square colorful tile with a vivid colored icon disc + label.

interface NbhFeatureTileProps {
  label: string;
  icon: LucideIcon;
  palette: NbhPaletteKey;
  to?: LinkOptions['to'];
  onClick?: () => void;
  badgeCount?: number;
}

export function NbhFeatureTile({
  label,
  icon: Icon,
  palette,
  to,
  onClick,
  badgeCount
}: NbhFeatureTileProps): JSX.Element {
  const p = nbhPalette(palette);
  const inner = (
    <div
      className={cn(
        'group relative flex aspect-square w-full flex-col items-center justify-center gap-vt-2 overflow-hidden rounded-vt-lg px-vt-2 py-vt-3 text-center',
        'shadow-vt-1 transition-all duration-vt ease-vt hover:-translate-y-0.5 hover:shadow-vt-2',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400'
      )}
      style={{ background: `linear-gradient(135deg, ${p.bgFrom} 0%, ${p.bgTo} 100%)` }}
    >
      <span aria-hidden="true" className="absolute -right-4 -bottom-4 h-14 w-14 rounded-full bg-white/30" />
      <span
        className="relative flex h-12 w-12 items-center justify-center rounded-full shadow-vt-2 ring-4 ring-white/60 transition-transform duration-vt ease-vt group-hover:scale-110"
        style={{ backgroundColor: p.fg }}
      >
        <Icon className="h-6 w-6 text-white" strokeWidth={2.5} />
      </span>
      <span
        className="relative line-clamp-2 text-vt-13 font-bold leading-tight tracking-[-0.01em]"
        style={{ color: p.fgDeep }}
      >
        {label}
      </span>
      {badgeCount && badgeCount > 0 ? (
        <span className="absolute right-2 top-2 inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-full border-[2px] border-white bg-vt-alert px-[5px] text-[10px] font-extrabold leading-none text-white shadow-vt-1">
          {badgeCount > 9 ? '9+' : String(badgeCount)}
        </span>
      ) : null}
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="block">
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className="block w-full">
      {inner}
    </button>
  );
}

// ─── NbhGreeting ──────────────────────────────────────────────────

interface NbhGreetingProps {
  greeting: string;
  status?: string;
  className?: string;
}

export function NbhGreeting({ greeting, status, className }: NbhGreetingProps): JSX.Element {
  return (
    <div className={cn('px-vt-1', className)}>
      <div className="text-vt-20 font-bold tracking-[-0.02em] text-vt-text">{greeting}</div>
      {status ? <div className="mt-0.5 text-vt-13 font-medium text-vt-text-muted">{status}</div> : null}
    </div>
  );
}

// ─── NbhActivityRow ───────────────────────────────────────────────

interface NbhActivityRowProps {
  icon: LucideIcon;
  palette: NbhPaletteKey;
  text: string;
  meta?: string;
  to?: LinkOptions['to'];
  onClick?: () => void;
  trailing?: React.ReactNode;
}

export function NbhActivityRow({
  icon: Icon,
  palette,
  text,
  meta,
  to,
  onClick,
  trailing
}: NbhActivityRowProps): JSX.Element {
  const p = nbhPalette(palette);
  const body = (
    <div className="group flex items-center gap-vt-3 rounded-vt-sm px-vt-2 py-vt-2 transition-colors duration-vt hover:bg-vt-surface-2">
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full shadow-vt-1 ring-2 ring-white/60"
        style={{ backgroundColor: p.fg }}
      >
        <Icon className="h-4 w-4 text-white" strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-vt-14 font-semibold tracking-[-0.01em] text-vt-text">{text}</div>
        {meta ? <div className="mt-0.5 truncate text-vt-12 text-vt-text-muted">{meta}</div> : null}
      </div>
      {trailing}
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="block">
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="block w-full text-left">
        {body}
      </button>
    );
  }
  return body;
}
